import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * Filter/toolbar for portal list pages: Active / Past / All grouping tabs,
 * an optional status dropdown, an optional date range, and search.
 *
 * @param groups  [{ id, label }]  the Active/Past/All toggle
 * @param group   current group id
 * @param onGroupChange
 * @param statusOptions [{ value, label }] (optional explicit status select)
 * @param status / onStatusChange
 * @param showDates boolean
 * @param fromDate/toDate/onFromDateChange/onToDateChange
 * @param search/onSearchChange
 * @param rightSlot  optional node (e.g. a "New inquiry" button)
 */
const PortalFilterBar = ({
  groups = [],
  group,
  onGroupChange,
  statusOptions,
  status,
  onStatusChange,
  showDates = true,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  search,
  onSearchChange,
  rightSlot,
}) => {
  const { t } = useTranslation();
  const c = t('portal.common', { returnObjects: true });

  const inputStyle = {
    backgroundColor: UI_COLORS.FORMS.INPUT.background,
    borderColor: UI_COLORS.FORMS.INPUT.border,
    color: UI_COLORS.FORMS.INPUT.text,
  };

  return (
    <div className="mb-4 space-y-3">
      {/* Active / Past / All tabs */}
      {groups.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {groups.map((g) => {
            const active = group === g.id;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => onGroupChange(g.id)}
                className="px-4 py-1.5 rounded-full text-sm font-semibold transition-all"
                style={{
                  color: active ? UI_COLORS.BUTTONS.PRIMARY.text : UI_COLORS.TEXT.SECONDARY,
                  backgroundColor: active ? UI_COLORS.BRAND.TERTIARY : colorHelpers.withOpacity(UI_COLORS.BRAND.SECONDARY, 0.06),
                }}
              >
                {g.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        {onSearchChange && (
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{c?.search || 'Search'}</label>
            <input
              type="text"
              value={search || ''}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={c?.searchPlaceholder || 'Search…'}
              className="w-full px-3 py-2 border-2 rounded-lg text-sm"
              style={inputStyle}
            />
          </div>
        )}

        {statusOptions && (
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{c?.status || 'Status'}</label>
            <select
              value={status || ''}
              onChange={(e) => onStatusChange(e.target.value)}
              className="px-3 py-2 border-2 rounded-lg text-sm"
              style={inputStyle}
            >
              {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}

        {showDates && (
          <>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{c?.from || 'From'}</label>
              <input type="date" value={fromDate || ''} onChange={(e) => onFromDateChange(e.target.value)} className="px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{c?.to || 'To'}</label>
              <input type="date" value={toDate || ''} onChange={(e) => onToDateChange(e.target.value)} className="px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
            </div>
          </>
        )}

        {rightSlot && <div className="ml-auto">{rightSlot}</div>}
      </div>
    </div>
  );
};

export default PortalFilterBar;
